// Storefront embed entry point — tiny router.
//
// Canonical customer path (2026-05): TRIGGER mode → fullscreen overlay.
// On page load we ship almost nothing: this entry, `injectTriggerStyles`,
// and the mountTriggerButton CTA below. The heavy customizer chunk
// (preview + form + overlay CSS) only downloads when the customer
// hovers / focuses / clicks "Personalizar", or via the idle prefetch.
//
// El modo en línea LEGADO (`data-mode="inline"`, otra implementación
// entera del personalizador) se retiró en sep-2026: nada lo usaba y su
// existencia costó dos incidentes —la analítica y el snapshot de rescate
// vivieron meses sólo dentro de él—. Un host que todavía lo pida cae en
// el camino normal. Lo que sigue vivo, y comparte runtime con el
// be removed once Dondefue's storefronts have all migrated.
// See FRONTEND_PLAN.md §F5 and DECISIONS.md §D-15.
//
// Mount data attributes:
//   data-shop          dondefue.myshopify.com           (required)
//   data-handle        product handle                   (storefront mode)
//   data-template-id   force a specific template        (admin demo)
//   data-variant       Shopify variant id              (current selection)
//   data-api           https://app.example.com         (required)
//   data-mode          'trigger' | 'inline-v2' | 'inline'
//                      (default trigger; inline-v2 = customizer in-page
//                       over the unified runtime; inline = legacy)

// Sólo la hoja de page-load (botón + barra sticky + bloqueo del CTA
// nativo). La hoja completa del formulario —~165 KB de literales que
// Rollup no puede tree-shakear— la inyecta cada presentación desde su
// propio chunk perezoso. Ver styles/trigger.ts y bundle-budget.test.ts.
import { injectTriggerStyles } from './styles/trigger.js';
import {
  startAutoMount,
  fetchTriggerContext,
  fetchEngineKind,
  soltarVeloDeApertura,
  type ContextoDelBoton,
  type MountDeps,
  type TriggerMount,
} from './auto-mount.js';
import { isOwnedDesign } from './brandeate/owned-designs.js';
import { startSettingsBridge, getCurrentSettings, getValidBrandSwatches, elCopyDelComercioAplica } from './personalizer-settings/index.js';
import { detectarYFijarLocale } from './i18n-overrides.js';
import { etiquetaTrigger, etiquetaResume } from './trigger-labels.js';
import { startPreviewBridge } from './preview-bridge.js';
// El emisor de analítica va en el entry (no en el chunk perezoso)
// porque `trigger_shown` —la impresión del botón— es el denominador
// de todo el embudo y ocurre antes de que exista chunk que cargar.
// Es un módulo sin dependencias pesadas; mide ~3 KB en el bundle.
import { initTracker, track, flushTracker } from './analytics/tracker.js';
import { avisarDePagina } from './analytics/pageview.js';
import { applyThemeButtonStyle, mountStickyCta } from './trigger-cta.js';
// Módulo sin dependencias: leer el borrador con `loadDraft` habría
// traído el saneador de render-core al entry. Ver draft-flag.ts.
import { hasEditedDraft } from './draft-flag.js';
// «Tu diseño» como foto de portada de la ficha en la visita siguiente.
// Aquí sólo entra el ALMACÉN, que no importa nada (por el mismo motivo
// que draft-flag.ts) y hace falta en el primer pintado para decidir el
// texto del botón. Quien pinta la foto —`vitrina.ts`, que se sabe la
// galería del tema entera— llega por import dinámico: sólo se descarga
// en las fichas donde hay diseño guardado, que son las únicas donde
// pinta algo. Ver bundle-budget.test.ts.
import { hayPortada, leerPortada } from './portada-del-cliente.js';
// Subpath import on purpose — the ui-rows barrel reaches
// layer-style-rows → @personalizer/schema → zod, whose top-level
// z.object() calls Rollup can't tree-shake. Importing the barrel here
// used to drag ~27 KB gzip of schema+zod into the page-load bundle
// (see bundle-budget.test.ts, embed.js budget).
import { registerBrandSwatchesProvider } from '@personalizer/ui-rows/brand-palette';
import { escHtml } from '@personalizer/ui-rows/esc';
import { lazyChunk } from './stale-bundle.js';

// Chevron del CTA, inline a propósito: importar `icons.ts` desde el
// entry arrastraría el set completo (~3 kB) a `embed.js`, que se
// descarga en CADA page-view de producto. Ver bundle-budget.test.ts.
const TRIGGER_CHEVRON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';


// Wire the merchant brand-swatches provider once at boot.  The picker
// in @personalizer/ui-rows calls this on every mount, so a /personalizer
// edit reflects in the next picker open with no module-reload dance.
registerBrandSwatchesProvider(getValidBrandSwatches);

// Boot the admin↔embed field-focus bridge (Fase C4). No-op unless this
// mount is framed AND carries `?bridge=1` — a real storefront customer
// never sees either condition, so this is a zero-cost call there.
startPreviewBridge();

export const EMBED_VERSION = '0.1.0';

// ── Lazy module loaders ────────────────────────────────────────────────
// Cada módulo se carga con import() dinámico para que Vite lo saque del
// bundle de page-load. Memoizados vía `lazyChunk`, que además hace dos
// cosas que el memo de antes no hacía:
//
//   1. NO memoiza el rechazo — un fallo de red pasajero se arreglaba
//      solo si el cliente recargaba, porque el segundo clic reusaba la
//      promesa ya rota sin volver a pedir el chunk.
//   2. Si el chunk falla porque la pestaña quedó en un build anterior
//      al desplegado (sus chunks hasheados ya no existen en el
//      servidor), recarga la página una vez.
//
// Ver stale-bundle.ts: ahí está el caso real de producción que motiva
// todo esto.

type BrandeateModule = typeof import('./brandeate-customizer.js');
const loadBrandeateModule = lazyChunk<BrandeateModule>(() => import('./brandeate-customizer.js'));


type ThreeDModule = typeof import('./threed-overlay.js');
const loadThreeDModule = lazyChunk<ThreeDModule>(() => import('./threed-overlay.js'));

type MuroModule = typeof import('./muro/abrir.js');
const loadMuroModule = lazyChunk<MuroModule>(() => import('./muro/abrir.js'));

type VitrinaModule = typeof import('./integrations/shopify/vitrina.js');
const loadVitrinaModule = lazyChunk<VitrinaModule>(() => import('./integrations/shopify/vitrina.js'));

/** Renders the trigger CTA inside `host`. The button mirrors the
 *  merchant theme's own primary buy button (see trigger-cta.ts) and
 *  raises a sticky bottom-bar copy on long pages. Clicking it
 *  dynamic-loads the customizer module and opens its fullscreen overlay. */
function mountTriggerButton(host: HTMLElement, mount: TriggerMount): void {
  host.classList.add('dfp-trigger-root');
  // Fija el idioma PRONTO, aquí en el entry: el botón se pinta ahora, y
  // `resolveLocale` vive en el chunk perezoso (no corre hasta que se abre
  // el editor). Sin esto, en /pt-pt/ el botón salía «Personalizar ahora»
  // en castellano. Es el MISMO detector y el MISMO estado que usa `t()`
  // (ver detectarYFijarLocale). Idempotente: resolveLocale lo re-fija
  // igual al abrir.
  detectarYFijarLocale(host);
  // Friendly default the merchant can override via /admin/personalizer
  // (read by startSettingsBridge below) or a pinned data-cta-label. The
  // settings bridge wins last via the DOM label swap, matching the
  // merchant's admin choice — which is what they expect.
  const prs = getCurrentSettings();
  // ¿Dejó un diseño a medias en este dispositivo? Entonces el botón no
  // es una invitación a empezar, es la puerta de vuelta a su trabajo:
  // al cerrar el editor le prometimos que podía volver, y ésta es la
  // otra mitad de esa promesa. Ver draft-flag.ts (y por qué se lee la
  // bandera a mano en vez de cargar el borrador entero).
  //
  // La PORTADA guardada cuenta igual que el borrador, y además llega
  // antes: está indexada por handle —lo único que la ficha real trae en
  // el HTML—, mientras que el borrador vive bajo el id de plantilla,
  // que tarda una petición de red. Así el botón dice lo que tiene que
  // decir en el PRIMER pintado, y no un segundo después.
  // Se lee UNA vez: la foto son decenas de kB y aquí hace falta para
  // dos cosas —el texto del botón y la miniatura de la barra sticky—.
  const portada = leerPortada(mount.shop, mount.handle ?? '');
  const resuming = portada !== null
    || hasEditedDraft(mount.shop, mount.templateId ?? mount.handle ?? 'default');
  // El copy que el comercio escribió sólo aplica en SU idioma: en un
  // mercado de otro idioma (`textsLocale` ≠ el del cliente) sus rótulos en
  // es se descartan y mandan las bases por idioma. Es la misma regla del
  // guard de `applyPersonalizerText`, aplicada aquí porque este botón NO
  // pasa por ella: lee `prs` directo.
  const copyDelComercio = prs ? elCopyDelComercioAplica(prs) : true;
  const ctaLabel = host.dataset.ctaLabel
    ?? (resuming ? ((copyDelComercio && prs?.ctaResumeLabel) || etiquetaResume()) : null)
    ?? (copyDelComercio ? prs?.ctaLabel : null)
    ?? etiquetaTrigger();

  host.innerHTML = `
    <button type="button" class="dfp-trigger-cta">
      <span class="dfp-trigger-cta-label">${escHtml(ctaLabel)}</span>
      <span class="dfp-trigger-cta-arrow" aria-hidden="true">${TRIGGER_CHEVRON}</span>
    </button>
  `;

  const cta = host.querySelector<HTMLButtonElement>('.dfp-trigger-cta')!;

  // LA PORTADA DE LA FICHA ES SU DISEÑO. Si en este navegador quedó un
  // diseño de una visita anterior, entra como primera foto de la
  // galería del tema, con su distintivo y su vuelta al personalizador.
  // No-op en cualquier tema que no sea el de Donde Fue.
  //
  // Se pide el chunk sólo cuando HAY portada: en la inmensa mayoría de
  // las visitas no la hay, y esto corre en cada página de la tienda.
  // Va DESPUÉS de pintar el botón porque el atajo de la foto abre el
  // personalizador pulsándolo — un solo dueño de esa apertura, que es
  // quien sabe de motor 2D/3D, de variante y de analítica.
  if (portada) {
    void loadVitrinaModule()
      .then((m) => m.sincronizarVitrina({ shop: mount.shop, handle: mount.handle ?? '' }))
      .catch((err) => console.warn('[personalizer-embed] portada de la ficha', err));
  }
  // La marca que protege el texto de "continuar" de que el puente de
  // ajustes lo pise con el de "empezar" (ver personalizer-settings).
  // Con `data-cta-label` no se marca: ese texto manda siempre.
  if (resuming && !host.dataset.ctaLabel) cta.dataset.bdResume = '1';

  // «Continuar con tu diseño» se vuelve a decidir DOS veces más, y las
  // dos hacen falta:
  //  · cuando se sabe el id de la plantilla. La ficha real monta por
  //    `data-handle`, así que arriba `mount.templateId` es null y la
  //    bandera se buscaba en `dfp:draft:<tienda>:<handle>`… mientras el
  //    autoguardado escribe en `dfp:draft:<tienda>:<templateId>`. En la
  //    tienda el botón NUNCA cambiaba (medido el 11-sep-2026 en
  //    dondefue.com: borrador con `edited:true` y el botón en
  //    «Personalizar ahora» tras recargar).
  //  · al cerrar el personalizador, sin recargar: el aviso de cierre
  //    promete que se puede volver, y el botón de al lado es quien lo
  //    cumple (CONVENTIONS §10-bis).
  // El texto fijado por el comercio en `data-cta-label` manda siempre.
  const pintarComoContinuar = (): void => {
    if (host.dataset.ctaLabel) return;
    if (!hasEditedDraft(mount.shop, mount.templateId ?? mount.handle ?? 'default')
      && !hayPortada(mount.shop, mount.handle ?? '')) return;
    const s = getCurrentSettings();
    const label = ((s && elCopyDelComercioAplica(s) && s.ctaResumeLabel) || etiquetaResume());
    for (const b of document.querySelectorAll<HTMLElement>('.dfp-trigger-cta')) {
      const l = b.querySelector<HTMLElement>('.dfp-trigger-cta-label');
      if (l) l.textContent = label;
      b.dataset.bdResume = '1';
    }
  };
  window.addEventListener('bd:customizer-closed', pintarComoContinuar);

  // Resolve the customizer engine (2D personalizer vs 3D relief-map
  // studio) once, in the background, from the linked design's
  // `customerView.kind`. Memoised so the click just reads the settled
  // value; if the click beats the fetch we simply await it (user-
  // initiated, so a tiny wait is fine). Defaults to 2D on any failure.
  let contexto: ContextoDelBoton = { kind: '2d', studio: 'mapa', templateId: mount.templateId, seguro: false };
  let engineKind: '2d' | '3d' = '2d';
  const engineReady = fetchTriggerContext(mount)
    .then((ctx) => {
      const { kind, templateId } = ctx;
      contexto = ctx;
      engineKind = kind;
      // La ficha real llega SIN `data-template-id` —sólo con el
      // handle—, así que este es el primer sitio donde se sabe cuál es
      // la plantilla. Sin anotarlo, el estudio 3D abre sin plantilla y
      // se queda sin sesión de diseño: la placa se ve, pero el enlace
      // del cliente no existe. (El 2D lo resuelve por su cuenta en
      // customizer/runtime.ts.)
      if (templateId && !mount.templateId) mount.templateId = templateId;
      // Y con el id ya se puede leer la bandera del borrador DE VERDAD.
      pintarComoContinuar();
      // Con el id resuelto ya se puede atribuir la analítica. Se ata
      // aquí y no en el click: el overlay reutiliza el mismo emisor
      // (initTracker es idempotente por templateId), así que la sesión
      // del cliente es UNA sola desde que ve el botón hasta que compra.
      if (templateId) {
        initTracker({
          shop: mount.shop,
          apiBase: mount.apiBase,
          templateId,
          productHandle: mount.handle ?? undefined,
        });
        observeCtaImpression(cta);
      }
      return kind;
    })
    .catch(() => '2d' as const);


  // At click time, sync mount.variantId from whatever the PDP currently
  // says is selected — so the Formato step pre-selects what the shopper
  // already picked in the native variant picker. Theme-agnostic: reads
  // `?variant=<id>` from the URL (Shopify's canonical signal — the
  // native picker rewrites the URL on change in every modern theme), or
  // the hidden `[name="id"]` input of the product form as a fallback.
  // df:variant:changed below covers Donde Fue's bespoke fast path; this
  // handles every other theme + the case where the shopper picked the
  // variant BEFORE this listener was attached.
  const open = (): void => {
    syncVariantFromPage(mount);
    // Se espera al sondeo de fondo y, si aquél NO llegó a saber el
    // motor —red caída, API que no contesta—, se vuelve a preguntar
    // ahora. Un fallo de red no puede decidir qué personalizador se
    // abre: reproducido en la ficha real del mapa 3D con la consulta
    // abortada, el botón abría el personalizador 2D sobre un producto
    // que no tiene documento 2D, y sin un solo error.
    void engineReady
      .then(() => fetchEngineKind(mount, contexto))
      .then((kind) => {
        if (kind === '3d') openThreeDCustomizer(mount);
        else openPersonalizerOverlay(mount);
      });
  };
  cta.addEventListener('click', open);

  // PDP variant → cart sync. The theme's native variant picker stays
  // visible on personalizer-linked PDPs (the merchant wants shoppers to
  // pick a format before opening the customizer), but the native "Añadir
  // al carrito" is hidden — the ONLY cart path is the wizard, which reads
  // `mount.variantId` at submit time (see wizard.ts → submitToCart). The
  // theme fires `df:variant:changed` whenever a radio/select changes
  // (df-main-product.liquid → updateSelectedVariant). Mirror that choice
  // into `mount.variantId` so the wizard + cart use the variant the
  // shopper actually selected on the page — otherwise the customizer
  // would always cart the default variant ("carrito equivocado").
  //
  // Idempotent: the handler only assigns a scalar, so if the host is
  // re-scanned the duplicate listener is harmless. The wizard's own
  // Formato step also calls back via onVariantChange when the shopper
  // re-picks inside the overlay, so the two stay consistent.
  document.addEventListener('df:variant:changed', (e: Event) => {
    const detail = (e as CustomEvent).detail as { id?: string | number } | null;
    if (detail && detail.id != null) mount.variantId = String(detail.id);
  });

  // Warm the cache so click → first frame feels instant. We dynamic-
  // import the customizer chunk AND call its prefetch helper (which
  // pre-fetches the template JSON + map geometry + mockup image) in
  // parallel; both kick off without blocking page render.
  const prefetch = (): void => {
    // Merchant opt-out: /admin/personalizer "Precargar al pasar el ratón".
    if (getCurrentSettings()?.enablePrefetch === false) return;
    // 3D templates don't use the 2D customizer chunk — skip the warm-up
    // (the studio loads inside its own iframe on open). Until the engine
    // resolves we optimistically prefetch the 2D chunk (the common case).
    if (engineKind === '3d') return;
    void loadBrandeateModule().then((m) => m.prefetchBrandeateCustomizer(mount));
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(prefetch, { timeout: 1500 });
  } else {
    setTimeout(prefetch, 300);
  }
  cta.addEventListener('mouseenter', prefetch, { once: true });
  cta.addEventListener('focus', prefetch, { once: true });

  // Dress the CTA in the store's own primary-button look. Only when a
  // native buy button exists (real storefront PDP) do we also raise the
  // sticky bottom-bar copy — admin preview / standalone keep just the
  // in-flow button.
  const themed = applyThemeButtonStyle(cta);
  if (themed) {
    mountStickyCta({
      inlineCta: cta,
      label: ctaLabel,
      onClick: open,
      // Su diseño, si lo hay. La barra está a la vista mientras el botón
      // de la ficha no lo esté (antes de llegar a él o tras pasarlo), y
      // lo que le recuerda que esto ya es suyo es verlo.
      miniatura: portada?.img ?? null,
      onMount: (sticky) => {
        applyThemeButtonStyle(sticky);
        sticky.addEventListener('mouseenter', prefetch, { once: true });
      },
    });
  }
}

/** Emite `trigger_shown` cuando el botón entra DE VERDAD en pantalla.
 *
 *  Se usa IntersectionObserver y no un disparo al montar porque el CTA
 *  suele vivir por debajo del pliegue: contar impresiones al montar
 *  inflaría el denominador del embudo con gente que nunca llegó a ver
 *  el botón, y entonces "% que abre el personalizador" mediría el
 *  scroll de la ficha, no el atractivo del botón.
 *
 *  Una sola vez por sesión (`once`), y con degradación limpia: si el
 *  navegador no trae IntersectionObserver, se emite al momento — mejor
 *  un denominador algo alto que ninguno. */
function observeCtaImpression(cta: HTMLElement): void {
  const fire = (): void => { track('trigger_shown'); };
  if (typeof IntersectionObserver !== 'function') { fire(); return; }
  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      io.disconnect();
      fire();
      // El cliente puede tardar minutos en pulsar (o no pulsar nunca):
      // sin este vaciado la impresión se quedaría en la cola hasta el
      // siguiente evento, y en una sesión de rebote no hay siguiente.
      flushTracker();
    }
  }, { threshold: 0.5 });
  io.observe(cta);
  // La barra fija es el MISMO botón: en un móvil sale nada más cargar
  // (el de la ficha queda bajo el pliegue), y un clic suyo sin impresión
  // daría un «% que abre» por encima del 100 %. Oculta va trasladada
  // fuera de la pantalla, así que sólo cruza el umbral cuando se ve.
  const barra = document.querySelector<HTMLElement>('.dfp-sticky-cta-bar .dfp-sticky-cta');
  if (barra) io.observe(barra);
}

/** Reads the currently-selected variant from the page (URL param or
 *  product form input) and mirrors it into `mount.variantId`.  Called
 *  at click time so the Formato step pre-selects whatever the shopper
 *  picked in the native variant picker BEFORE opening the customizer.
 *  No-op when neither signal is present (admin demo, no native picker
 *  on the page). */
function syncVariantFromPage(mount: TriggerMount): void {
  try {
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant && /^\d+$/.test(urlVariant)) {
      mount.variantId = urlVariant;
      return;
    }
  } catch { /* malformed URL — skip */ }
  const input = document.querySelector<HTMLInputElement>(
    'form[action*="/cart/add"] [name="id"]',
  );
  const v = input?.value?.trim();
  if (v && /^\d+$/.test(v)) mount.variantId = v;
}

/** Engine-aware overlay opener for the AUTO-OPEN path (`?open=personalizer`
 *  o un enlace de correo con `?design=<token>`). Aquí no hay sondeo de
 *  fondo del que tirar, así que se pregunta en el momento — y con el
 *  MISMO reintento que el botón: `fetchEngineKind` vuelve a preguntar
 *  si la primera respuesta no supo el motor. Sin eso, un enlace de
 *  correo abierto con mala cobertura abría el personalizador 2D sobre
 *  un diseño 3D. */
function openOverlayAutoRouted(mount: TriggerMount): void {
  // `fetchEngineKind` anota la plantilla en el mount cuando la
  // resuelve: es aquí donde se sabe cuál es en una ficha que sólo trae
  // handle, y el estudio 3D la necesita para tener sesión de diseño (y
  // por tanto enlace).
  void fetchEngineKind(mount)
    .then((kind) => {
      if (kind === '3d') openThreeDCustomizer(mount);
      else openPersonalizerOverlay(mount);
    })
    .catch(() => openPersonalizerOverlay(mount));
}

// ── Una apertura EN VUELO a la vez ────────────────────────────────────
//
// Entre pedir abrir y que el overlay exista en el DOM hay una descarga
// —el chunk del personalizador—, y en ese hueco el `querySelector` de
// abajo no ve nada: dos peticiones seguidas abrían DOS personalizadores,
// uno encima del otro, cada uno con su lienzo, su horneado del cielo y
// su autoguardado. Medido el 10-sep-2026 en la Vista cliente del panel,
// que pulsa el botón por sondeo cada 250 ms hasta ver el overlay: dos
// overlays, dos shells, dos lienzos. En la tienda lo hace un doble toque
// en «Personalizar» desde un móvil lento, que es el caso normal.
//
// Se guarda la promesa de la apertura en curso y se suelta al terminar
// (bien o mal): mientras dure, las peticiones siguientes no hacen nada.
let aperturaEnVuelo: Promise<unknown> | null = null;

/** Al acabar una apertura, si no hay overlay en la página es que ha
 *  fallado (chunk perdido, error al montar): fuera el velo del enlace
 *  que abre, para que se vea la ficha y no una pantalla vacía. Si el
 *  overlay está, el velo lo quita el cargador del tema al verlo. */
function soltarVeloSiNoAbrio(): void {
  if (!document.querySelector('.bd-overlay, .dfp-3d-overlay')) soltarVeloDeApertura(document);
}

/** Opens the 3D relief-map studio in a fullscreen iframe overlay
 *  (lazy-loaded the first time). Used when the linked design is marked
 *  `customerView.kind === '3d'`. */
function openThreeDCustomizer(mount: TriggerMount): void {
  if (document.querySelector('.dfp-3d-overlay')) return;
  if (aperturaEnVuelo) return;
  console.info('[personalizer-embed] opening 3D studio overlay');
  aperturaEnVuelo = loadThreeDModule()
    .then((m) => m.openThreeDOverlay(mount))
    .catch((err) => {
      console.error('[personalizer-embed] 3D overlay failed', err);
    })
    .finally(() => { aperturaEnVuelo = null; soltarVeloSiNoAbrio(); });
}

/** Opens the Brandeate customizer (lazy-loaded the first time). */
function openPersonalizerOverlay(mount: TriggerMount): void {
  const existing = document.querySelector<HTMLElement>('.bd-overlay');
  if (existing) {
    existing.querySelector<HTMLButtonElement>('.bd-shell-close')?.focus();
    return;
  }
  if (aperturaEnVuelo) return;
  if (mount.muro) {
    aperturaEnVuelo = loadMuroModule()
      .then((m) => m.abrirMuro(mount))
      .catch((err) => console.error('[personalizer-embed] la pared no abre', err))
      .finally(() => { aperturaEnVuelo = null; soltarVeloSiNoAbrio(); });
    return;
  }
  console.info('[personalizer-embed] opening customizer overlay');
  // Catch + log rejections from the lazy chunk: si openBrandeateCustomizer
  // lanza síncrono (style spec mala, dependencia con módulos ESM rota),
  // el `void` sin catch lo dejaba como unhandledrejection silencioso y
  // el cliente seguía viendo el placeholder "Cargando personalizador..."
  // del preview.html sin pista alguna.
  aperturaEnVuelo = loadBrandeateModule()
    .then((m) => m.openBrandeateCustomizer(mount))
    .catch((err) => {
      console.error('[personalizer-embed] customizer overlay failed', err);
      const msg = err instanceof Error ? err.message : String(err);
      // Render fallback en cualquier #dfp-personalizer host para que el
      // merchant vea el error en vez del placeholder eterno.
      const host = document.querySelector<HTMLElement>('.dfp-personalizer, #dfp-personalizer');
      if (host) {
        host.innerHTML = `<pre class="dfp-err" style="padding:16px;background:#fee;color:#900;border-radius:6px;margin:48px;white-space:pre-wrap;font-family:ui-monospace,monospace;font-size:12px;">Personalizer: error abriendo el customizador.\n${escHtml(msg)}\n\nAbre DevTools (F12) → Consola para más detalles.</pre>`;
      }
    })
    .finally(() => { aperturaEnVuelo = null; soltarVeloSiNoAbrio(); });
}

// Mount lifecycle lives in ./auto-mount.ts (readiness gating + the
// live MutationObserver that catches lazily-rendered hosts). Here we
// only bind the real browser globals + the heavy-chunk loaders to it.
const mountDeps: MountDeps = {
  // Módulo sin dependencias a propósito: esto vive en el entry, que se
  // descarga en cada ficha de producto (ver owned-designs.ts).
  isOwnedDesign,
  doc: document,
  win: window,
  injectTriggerStyles,
  mountTrigger: mountTriggerButton,
  // Auto-open (email "Editar" links) must respect the 2d/3d engine too.
  openOverlay: openOverlayAutoRouted,
  // Late-host resilience: the loader liquid injects the host after an
  // async product→template check, so the synchronous bridge boot below
  // usually runs too early to see it.  auto-mount's observer reliably
  // catches the host; start the settings bridge from there too so the
  // merchant's saved brand logo/name/colours actually get fetched on the
  // live storefront (not just in admin preview, where the host is SSR'd).
  // startSettingsBridge dedups, so the redundant sync call is harmless.
  onHostMount: ({ apiBase, shop }) => {
    if (apiBase) void startSettingsBridge({ apiBase, shop });
  },
};
startAutoMount(mountDeps);

// Boot the personalizer-settings bridge: fetches merchant-saved copy
// (brand colours, CTA text, step labels, feature toggles) from
// /storefront/settings/personalizer and applies them DOM-wide.
//
// Reads `data-shop` + `data-api` from the FIRST personalizer host on
// the page — every storefront we ship has at most one. If neither
// attribute is present we skip the fetch (admin preview iframe will
// still feed us via postMessage).
{
  const firstHost = document.querySelector<HTMLElement>('.dfp-personalizer, #dfp-personalizer');
  const shop = firstHost?.dataset.shop ?? '';
  const apiBase = firstHost?.dataset.api ?? '';
  // Sólo con la tienda YA puesta en el host. En preview.html el
  // `data-shop` lo escribe un módulo que puede correr DESPUÉS de este
  // (un módulo insertado dinámicamente no espera a los del documento),
  // y el puente, que se ata una sola vez, arrancaba sin tienda: leía la
  // caché sin dominio y la vista previa de una tienda nueva salía con
  // el logo de otra. Sin tienda aquí, lo arranca `onHostMount`, que ya
  // llega con ella.
  if (apiBase && shop) {
    void startSettingsBridge({ apiBase, shop });
  }
}

// La visita a la tienda. Va al final del arranque y no antes: si algo
// de lo de arriba fallara, lo que no puede pasar es que además se
// pierda la medida. Se cuenta en TODAS las páginas, también en las que
// no tienen personalizador — que son el 94 % del tráfico y eran las que
// no se veían. No hace nada fuera de una tienda de Shopify.
avisarDePagina();

// En el CARRITO, «Editar mi diseño» abre el personalizador con el diseño
// de esa línea en vez de llevar a la ficha a secas. Chunk aparte y sólo
// en /cart: el resto de páginas no descargan ni un byte de esto.
if (/\/cart\/?$/.test(location.pathname)) {
  void lazyChunk(() => import('./carrito/enlaces-del-carrito.js'))()
    .then((m) => m.arreglarEnlacesDelCarrito())
    .catch((err) => console.warn('[personalizer-embed] enlaces del carrito', err));
  void lazyChunk(() => import('./carrito/persona-en-el-carrito.js'))()
    .then((m) => m.fijarPersonasDelCarrito())
    .catch((err) => console.warn('[personalizer-embed] persona del carrito', err));
}

console.info('[personalizer-embed] loaded', EMBED_VERSION);
